// API base URL - nginx proxies /api/ to backend. Exported for transports that
// cannot go through ApiClient (the XHR attachment upload builds its own URLs).
export const API_BASE = '/api/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: string
  ) {
    super(`API Error ${status}: ${body}`);
    this.name = 'ApiError';
  }
}

/** Extract a human-readable error message, falling back to a default. */
export function extractErrorMessage(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

type OnUnauthorized = () => void;
type TokenProvider = () => string | null;

/** Per-request overrides. */
export interface RequestOptions {
  /**
   * Skip the global onUnauthorized handler for this request. Used by session
   * revalidation (`/auth/me`), which owns its own 401 handling and must not
   * trigger the app-wide hard redirect.
   */
  suppressUnauthorized?: boolean;
}

export class ApiClient {
  private baseUrl: string;
  private token: string | null = null;
  private tokenProvider: TokenProvider | null = null;
  private onUnauthorized: OnUnauthorized | null = null;

  setOnUnauthorized(cb: OnUnauthorized | null): void {
    this.onUnauthorized = cb;
  }

  /**
   * Fire the global session-expiry handler. `handleErrorResponse` uses it for
   * fetch 401s; transports that bypass fetch (the XHR attachment upload) call
   * it directly so their 401s are treated identically.
   */
  notifyUnauthorized(): void {
    this.onUnauthorized?.();
  }

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Restore token from localStorage immediately on construction.
    // Zustand persist stores auth state under 'auth-storage' key.
    // This ensures the token is available BEFORE any React component renders,
    // preventing the race condition where useEffect sets the token too late.
    // This is a fallback: once setTokenProvider() is wired at app startup, the
    // token is read live from the store on every request instead.
    try {
      const stored = localStorage.getItem('auth-storage');
      if (stored) {
        const parsed = JSON.parse(stored);
        const storedToken = parsed?.state?.accessToken;
        if (storedToken) {
          this.token = storedToken;
        }
      }
    } catch {
      // localStorage not available or parse error - token stays null
    }
  }

  /**
   * Register a live token source (the auth store). When set, the Authorization
   * header is derived from it on every request — so the token attached always
   * matches current auth state, with no lag between the store updating and a
   * React effect calling setToken. Removes the login-time revalidation race.
   */
  setTokenProvider(provider: TokenProvider | null): void {
    this.tokenProvider = provider;
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  getToken(): string | null {
    // Prefer the live store (via provider); fall back to the eagerly-read
    // localStorage token when the provider yields nothing. That window is the
    // hard-navigation race: zustand persist rehydrates asynchronously, so the
    // provider returns null for a tick or two after a fresh page load — during
    // which the eager token (read in the constructor) keeps requests authed and
    // a valid session from 401ing. `setToken` keeps this fallback in sync on
    // login/logout, so it never resurrects a stale token.
    return this.tokenProvider?.() ?? this.token;
  }

  private getHeaders(): HeadersInit {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  private async handleErrorResponse(
    response: Response,
    body: string,
    options?: RequestOptions
  ): Promise<never> {
    if (response.status === 401 && !options?.suppressUnauthorized) {
      this.notifyUnauthorized();
    }
    throw new ApiError(response.status, body);
  }

  private async handleResponse<T>(response: Response, options?: RequestOptions): Promise<T> {
    if (!response.ok) {
      const body = await response.text();
      return this.handleErrorResponse(response, body, options);
    }

    // Handle empty responses
    const text = await response.text();
    if (!text) {
      return {} as T;
    }

    try {
      return JSON.parse(text) as T;
    } catch {
      return {} as T;
    }
  }

  async get<T>(path: string, options?: RequestOptions): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    return this.handleResponse<T>(response, options);
  }

  async post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });
    return this.handleResponse<T>(response, options);
  }

  async put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });
    return this.handleResponse<T>(response, options);
  }

  async patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });
    return this.handleResponse<T>(response, options);
  }

  async delete(path: string, options?: RequestOptions): Promise<void> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    if (!response.ok) {
      const body = await response.text();
      return this.handleErrorResponse(response, body, options);
    }
  }

  /** DELETE that returns a JSON response body (e.g. { deleted_count }). */
  async deleteJson<T>(path: string, options?: RequestOptions): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include',
    });
    return this.handleResponse<T>(response, options);
  }
}

export const api = new ApiClient(API_BASE);
